/**
 * Reader Mode banner — shown app-wide whenever the license is not VALID.
 * Fetches /license/status instead of reading a template global, and re-polls
 * on an interval, so a background license renewal is reflected without a
 * full page reload.
 */

const SECURITY_ALERT_MODES = new Set(['INVALID', 'MALFORMED']);

// Status check is low-stakes (not a security boundary, just banner freshness)
// so a low-frequency poll is enough; avoids hammering /license/status.
const STATUS_POLL_INTERVAL_MS = 5 * 60 * 1000;

function readerModeMessage(licenseMode) {
  switch (licenseMode) {
    case 'NEVER_VALIDATED':
      return 'No license has been installed. The app is in Reader Mode — view and export only.';
    case 'BEFORE_START':
      return 'Your license is not active yet. The app is in Reader Mode — view and export only.';
    case 'EXPIRED':
      return 'Your license has expired. The app is in Reader Mode — view and export only.';
    case 'TTL_EXPIRED':
      return 'Your license file is stale and needs to be renewed. The app is in Reader Mode — view and export only.';
    case 'INVALID':
      return 'Your license failed validation. This has been flagged as a security alert. The app is in Reader Mode — view and export only.';
    default:
      return 'Reader Mode is active — view and export only.';
  }
}

// Idempotent: a VALID result must hide an already-visible banner from a prior
// poll (e.g. a background license renewal), not just skip showing one.
function renderBannerState(banner, messageEl, mode) {
  if (!mode || mode === 'VALID') {
    banner.classList.add('d-none');
    banner.classList.remove('alert-warning', 'alert-danger');
    document.documentElement.style.setProperty('--reader-mode-banner-height', '0px');
    return;
  }

  messageEl.textContent = readerModeMessage(mode);
  banner.classList.remove('d-none', 'alert-warning', 'alert-danger');
  banner.classList.add(SECURITY_ALERT_MODES.has(mode) ? 'alert-danger' : 'alert-warning');

  if (window.lucide) {
    window.lucide.createIcons({ nodes: [banner] });
  }

  trackBannerHeight(banner);
}

function fetchAndRenderLicenseStatus(banner, messageEl) {
  return fetch('/license/status', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
    .then((response) => {
      if (!response.ok) {
        throw new Error(`License status request failed: ${response.status}`);
      }
      return response.json();
    })
    .then((data) => {
      // LicenseMode enum values come back lowercase (e.g. "never_validated").
      const mode = data && data.license_mode ? data.license_mode.toUpperCase() : '';
      renderBannerState(banner, messageEl, mode);
    })
    .catch((error) => {
      // The license route may not be deployed yet, or the request failed transiently.
      // Staying as-is (not flipping state on a transient error) is the safe default.
      console.warn('Reader Mode banner: could not load license status', error);
    });
}

export function initializeReaderModeBanner() {
  if (!window.userAuthenticated) return;

  const banner = document.getElementById('readerModeBanner');
  const messageEl = document.getElementById('readerModeBannerMessage');
  if (!banner || !messageEl) return;

  let pollInFlight = false;
  const pollLicenseStatus = () => {
    if (pollInFlight) return;
    pollInFlight = true;
    fetchAndRenderLicenseStatus(banner, messageEl).finally(() => {
      pollInFlight = false;
    });
  };

  pollLicenseStatus();
  setInterval(pollLicenseStatus, STATUS_POLL_INTERVAL_MS);
}

// The banner is fixed-position, so the header and page content shift down by
// its height to stay clear of it (see _header.scss, _page-layout.scss).
// Message length varies per license mode, so measure rather than hardcode.
// Each poll cycle can re-show the banner, so the observer is set up once per
// banner element rather than re-attached on every render pass.
let bannerResizeObserver = null;

function trackBannerHeight(banner) {
  const updateHeight = () => {
    document.documentElement.style.setProperty('--reader-mode-banner-height', `${banner.offsetHeight}px`);
  };

  updateHeight();

  if (!bannerResizeObserver) {
    bannerResizeObserver = new ResizeObserver(updateHeight);
    bannerResizeObserver.observe(banner);
  }
}
